import { useCallback, useEffect, useRef } from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';
import type { RelayError } from '@confluence/relay-errors/entry-points/RelayError.ts';

import { classifyGraphError, reportHandledGraphError } from '../utils/graphErrorReporting.ts';

import {
	getRelatedContentFailedEvent,
	getRelatedContentRequestedEvent,
	getRelatedContentSucceededEvent,
} from './relatedContentAnalytics.ts';
import { useQueryLoadingDuration } from './useQueryLoadingDuration.ts';

export type RelatedContentAnalyticsOptions = {
	enabled: boolean;
	identity: string;
	loading: boolean;
	data: unknown;
	error: RelayError | undefined;
};

export type RelatedContentHydrationObservation = {
	succeeded: (counts: { resolvedCount: number; totalCount: number }) => void;
};

export const useRelatedContentAnalytics = ({
	enabled,
	identity,
	loading,
	data,
	error,
}: RelatedContentAnalyticsOptions): (() => RelatedContentHydrationObservation) => {
	const { createEvent } = useConfluenceAnalyticsEvents();
	const hasRequested = useRef(false);
	const hasSucceeded = useRef(false);
	const hasReportedError = useRef(false);
	const getQueryDuration = useQueryLoadingDuration({
		enabled,
		identity,
		loading,
		result: data,
		error,
	});
	const queryDurationReader = useRef(getQueryDuration);
	useEffect(() => {
		queryDurationReader.current = getQueryDuration;
	}, [getQueryDuration]);
	const fire = useCallback(
		(event: Parameters<typeof createEvent>[0]) => {
			try {
				createEvent(event).fire();
			} catch {
				// Reporting must not prevent Related content from becoming available. CCINT-9636.
			}
		},
		[createEvent],
	);

	useEffect(() => {
		if (!enabled || hasRequested.current) {
			return;
		}
		hasRequested.current = true;
		fire(getRelatedContentRequestedEvent());
	}, [enabled, fire]);

	useEffect(() => {
		if (!enabled || !error) {
			hasReportedError.current = false;
			return;
		}
		if (hasReportedError.current) {
			return;
		}
		hasReportedError.current = true;
		reportHandledGraphError(error);
		fire(
			getRelatedContentFailedEvent({
				errorType: classifyGraphError(error),
				hasQueryData: data != null,
				...getQueryDuration(data, error),
			}),
		);
	}, [data, enabled, error, fire, getQueryDuration]);

	return useCallback(() => {
		const startedAt = performance.now();
		const queryDuration = queryDurationReader.current(data, error);
		return {
			succeeded: (counts) => {
				if (hasSucceeded.current || error) {
					return;
				}
				hasSucceeded.current = true;
				const durationMs = performance.now() - startedAt;
				const hydrationDurationMs = Number.isFinite(durationMs)
					? Math.max(0, Math.round(durationMs))
					: undefined;
				fire(
					getRelatedContentSucceededEvent({
						...counts,
						...queryDuration,
						hydrationDurationMs,
					}),
				);
			},
		};
	}, [data, error, fire]);
};
